/**
 * Utility para detectar el tipo de plataforma/dispositivo
 * Permite decidir qué tipo de almacenamiento local utilizar
 */

/**
 * Tipos de plataforma soportados
 */
export const PlatformType = {
  WEB: 'web',
  IOS: 'ios',
  ANDROID: 'android',
  UNKNOWN: 'unknown'
};

/**
 * Tipos de almacenamiento según la plataforma
 */
export const StorageType = {
  INDEXEDDB: 'indexeddb',    // Para Web
  SQLITE: 'sqlite',           // Para iOS/Android
  MEMORY: 'memory'            // Fallback en memoria (temporal)
};

/**
 * Detecta el tipo de plataforma actual
 * @returns {string} PlatformType
 */
export const detectPlatform = () => {
  // Verificar si estamos en un entorno de navegador
  if (typeof window === 'undefined') {
    return PlatformType.UNKNOWN;
  }

  const userAgent = window.navigator.userAgent.toLowerCase();
  const platform = window.navigator.platform?.toLowerCase() || '';

  // Detectar React Native
  if (window.ReactNativeWebView || userAgent.includes('reactnative')) {
    // Verificar si es iOS
    if (/(iphone|ipad|ipod)/.test(userAgent) || platform.includes('iphone') || platform.includes('ipad')) {
      return PlatformType.IOS;
    }
    // Verificar si es Android
    if (/android/.test(userAgent)) {
      return PlatformType.ANDROID;
    }
  }

  // Detectar iOS (Safari, Chrome en iOS, etc.)
  if (/(iphone|ipad|ipod)/.test(userAgent) || platform.includes('iphone') || platform.includes('ipad')) {
    // Si no es React Native, es navegador web en iOS
    return PlatformType.WEB;
  }

  // Detectar Android (navegador web)
  if (/android/.test(userAgent) && !window.ReactNativeWebView) {
    return PlatformType.WEB;
  }

  // Por defecto, es Web (navegador de escritorio)
  return PlatformType.WEB;
};

/**
 * Determina el tipo de almacenamiento a utilizar según la plataforma
 * @returns {string} StorageType
 */
export const getStorageType = () => {
  const platform = detectPlatform();

  switch (platform) {
    case PlatformType.IOS:
    case PlatformType.ANDROID:
      return StorageType.SQLITE;
    
    case PlatformType.WEB:
      // Verificar si IndexedDB está disponible
      if (window.indexedDB) {
        return StorageType.INDEXEDDB;
      }
      // Fallback a memoria si IndexedDB no está disponible
      return StorageType.MEMORY;
    
    default:
      return StorageType.MEMORY;
  }
};

/**
 * Verifica si la plataforma es móvil (iOS o Android)
 * @returns {boolean}
 */
export const isMobile = () => {
  const platform = detectPlatform();
  return platform === PlatformType.IOS || platform === PlatformType.ANDROID;
};

/**
 * Verifica si la plataforma es Web
 * @returns {boolean}
 */
export const isWeb = () => {
  return detectPlatform() === PlatformType.WEB;
};

/**
 * Verifica si IndexedDB está disponible
 * @returns {boolean}
 */
export const isIndexedDBAvailable = () => {
  return typeof window !== 'undefined' && !!window.indexedDB;
};

/**
 * Obtiene información detallada de la plataforma
 * @returns {Object}
 */
export const getPlatformInfo = () => {
  const platform = detectPlatform();
  const storageType = getStorageType();
  
  return {
    platform,
    storageType,
    isMobile: isMobile(),
    isWeb: isWeb(),
    userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'unknown',
    hasIndexedDB: isIndexedDBAvailable(),
    hasLocalStorage: typeof window !== 'undefined' && !!window.localStorage,
    hasSessionStorage: typeof window !== 'undefined' && !!window.sessionStorage
  };
};

/**
 * Log de información de la plataforma (para debugging)
 */
export const logPlatformInfo = () => {
  const info = getPlatformInfo();
  console.group('🔍 Platform Detection');
  console.log('Platform:', info.platform);
  console.log('Storage Type:', info.storageType);
  console.log('Is Mobile:', info.isMobile);
  console.log('Is Web:', info.isWeb);
  console.log('Has IndexedDB:', info.hasIndexedDB);
  console.log('Has LocalStorage:', info.hasLocalStorage);
  console.log('User Agent:', info.userAgent);
  console.groupEnd();
  return info;
};

export default {
  PlatformType,
  StorageType,
  detectPlatform,
  getStorageType,
  isMobile,
  isWeb,
  isIndexedDBAvailable,
  getPlatformInfo,
  logPlatformInfo
};



